import { useState } from 'react';
import { api, callLimitsApi } from '../../api/client';
import { Button, Card, Chip, Field, Notice, SelectField } from '../../components/ui';
import { dateOnly, fullWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Calls abroad (Billing phase 2a, 3186; docs/rating.md):
 * - the dial plan that pauses calls abroad (made on the phone system, without international or
 *   premium-rate routes; Stitch puts each extension of a paused company on it, and back after);
 * - the rate tables stitch-rating prices those calls from: upload the carrier's rate deck as CSV
 *   (prefix, destination, cost[, price]) with a markup, from a day;
 * - calls with no rate (their invoices wait), and companies paused or with a problem.
 */
export function CallsAbroadSystemCard() {
  const overview = useData(() => callLimitsApi.overview(), [], { live: false });
  const tables = useData(() => callLimitsApi.rateTables(), [], { live: false });
  const unrated = useData(() => callLimitsApi.unrated(), [], { live: false });
  const customers = useData(() => api.customers(), [], { live: false });
  const [tenant, setTenant] = useState('');
  const [plans, setPlans] = useState<{ id: string; name: string }[] | null>(null);
  const [plan, setPlan] = useState('');
  const [csv, setCsv] = useState<{ name: string; text: string } | null>(null);
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [markup, setMarkup] = useState('30');
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState('');
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const s = overview.data?.settings;
  const tenants = (customers.data?.customers ?? []).filter((c) => c.tenant).map((c) => ({ value: c.tenant!, label: `${c.name} (${c.tenant})` }));

  async function run(what: string, fn: () => Promise<string>) {
    setBusy(what);
    setSaid(null);
    try {
      setSaid({ tone: 'ok', text: await fn() });
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy('');
    }
  }

  const newest = tables.data?.tables[0];
  return (
    <Card title="Calls abroad" action={s ? <Chip tone={s.pausePlan ? 'ok' : 'warn'}>{s.pausePlan ? 'Pausing ready' : 'No pause plan'}</Chip> : undefined}>
      <div className="card-body section-stack">
        <p className="muted small">
          Calls abroad and to premium-rate numbers are priced from the rate table below and paused for a company that reaches its limit (or hasn’t had them turned on) by putting
          its extensions on a dial plan without those routes. Each company’s switch and limits are on its page under Customers.
        </p>

        <section className="stack">
          <h3 className="section-title">The dial plan that pauses them</h3>
          <p className="muted small">
            {s?.pausePlan ? `Now: ${s.pausePlanName || s.pausePlan}.` : 'None chosen: companies can’t be paused until one is.'} Make it on the phone system first (only US and Canada routes, and
            emergency calls), then choose it here.
          </p>
          <div className="row-actions">
            <SelectField label="Read the dial plans from" value={tenant} onChange={(e) => setTenant(e.target.value)} options={[{ value: '', label: 'Choose a tenant' }, ...tenants]} />
            <Button
              variant="ghost"
              disabled={!tenant || Boolean(busy)}
              onClick={() =>
                void run('plans', async () => {
                  const r = await callLimitsApi.dialPlans(tenant);
                  setPlans(r.plans);
                  return `${r.plans.length} dial plans on ${tenant}.`;
                })
              }
            >
              Read them
            </Button>
          </div>
          {plans && (
            <div className="row-actions">
              <SelectField label="Dial plan" value={plan} onChange={(e) => setPlan(e.target.value)} options={[{ value: '', label: 'Choose one' }, ...plans.map((p) => ({ value: p.id, label: p.name || p.id }))]} />
              <Button
                disabled={!plan || Boolean(busy)}
                onClick={() =>
                  void run('save-plan', async () => {
                    const p = plans.find((x) => x.id === plan)!;
                    await callLimitsApi.setPausePlan({ id: p.id, name: p.name });
                    overview.reload();
                    return `Paused companies’ extensions go on “${p.name || p.id}”.`;
                  })
                }
              >
                Use it to pause
              </Button>
            </div>
          )}
        </section>

        <section className="stack">
          <h3 className="section-title">Rate tables</h3>
          {tables.data && !tables.data.available ? (
            <Notice tone="warn">The rating service isn’t answering (see the Rating card), so rate tables can’t be read or added.</Notice>
          ) : newest ? (
            <p className="muted small">
              {`In use from ${dateOnly(`${newest.effectiveFrom}T12:00:00`)}: table ${newest.version}, ${newest.rates.toLocaleString('en-US')} rates${newest.markupPercent ? `, ${newest.markupPercent}% over cost` : ''}${newest.source ? ` (${newest.source})` : ''}, added by ${newest.createdBy}.`}
            </p>
          ) : (
            <Notice tone="warn">No rate table yet: every call abroad is unrated, and those companies’ invoices wait. Upload the carrier’s rate deck.</Notice>
          )}
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              if (!csv) return;
              void run('upload', async () => {
                const r = await callLimitsApi.addRateTable({ effectiveFrom: from, markupPercent: Number(markup) || 0, source: source || csv.name, note: '', csv: csv.text });
                setCsv(null);
                tables.reload();
                unrated.reload();
                return `Table ${r.table.version}: ${r.table.rates.toLocaleString('en-US')} rates from ${dateOnly(`${from}T12:00:00`)}.${r.nowRated ? ` ${r.nowRated} calls with no rate are priced now.` : ''}`;
              });
            }}
          >
            <div className="field">
              <label htmlFor="rate-deck">Rate deck (CSV: prefix, destination, cost in cents[, price in cents])</label>
              <input
                id="rate-deck"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return setCsv(null);
                  void f.text().then((text) => setCsv({ name: f.name, text }));
                }}
              />
            </div>
            <div className="row-actions">
              <Field label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              <Field label="Markup over cost (%)" inputMode="decimal" value={markup} onChange={(e) => setMarkup(e.target.value)} hint="For lines with a cost and no price" />
              <Field label="Source" value={source} onChange={(e) => setSource(e.target.value)} placeholder="Carrier deck, November" />
            </div>
            <div className="row-actions">
              <Button type="submit" disabled={!csv || Boolean(busy)}>
                Add rate table
              </Button>
            </div>
          </form>
        </section>

        {(unrated.data?.unrated.length ?? 0) > 0 && (
          <section className="stack">
            <h3 className="section-title">Calls with no rate</h3>
            <p className="muted small">Their invoices wait until a rate covers them. Add the prefix to the rate table (or as the company’s own price).</p>
            <table className="table compact">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Numbers starting</th>
                  <th>Calls</th>
                  <th>Minutes</th>
                  <th>Latest</th>
                </tr>
              </thead>
              <tbody>
                {unrated.data!.unrated.map((u) => (
                  <tr key={`${u.account}-${u.prefix}`}>
                    <td>{u.name}</td>
                    <td>+{u.prefix}</td>
                    <td>{u.calls}</td>
                    <td>{u.minutes.toLocaleString('en-US')}</td>
                    <td>{fullWhen(u.last)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {(overview.data?.companies.length ?? 0) > 0 && (
          <section className="stack">
            <h3 className="section-title">Paused, let through, or with a problem</h3>
            <ul className="plain-list">
              {overview.data!.companies.map((c) => (
                <li key={c.customerId}>
                  <strong>{c.name}</strong>
                  {': '}
                  {c.problem ?? (c.paused === 'off' ? 'not turned on' : c.paused ? `paused (${c.paused} limit)` : c.overrideUntil ? `let through until ${fullWhen(c.overrideUntil)}` : '')}
                </li>
              ))}
            </ul>
          </section>
        )}
        {said && <Notice tone={said.tone}>{said.text}</Notice>}
      </div>
    </Card>
  );
}
